import React from 'react'
import { useLocation, Link as RouterLink } from 'react-router-dom'
import {
  Breadcrumbs as MUIBreadcrumbs,
  Link,
  Typography,
  Box,
  useTheme,
} from '@mui/material'
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
} from '@mui/icons-material'

interface BreadcrumbItem {
  label: string
  path: string
  icon?: React.ReactElement
}

interface BreadcrumbsProps {
  maxItems?: number
  showHome?: boolean
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ 
  maxItems = 8, 
  showHome = true 
}) => {
  const location = useLocation()
  const theme = useTheme()

  // Route to breadcrumb mapping
  const routeMap: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/catalogues': 'Catalogues',
    '/collections': 'Collections',
    '/categories': 'Categories',
    '/products': 'Products',
    '/products/add': 'Add Product',
    '/products/bulk': 'Bulk Operations',
    '/inventory': 'Inventory Dashboard',
    '/inventory/balancing': 'Inventory Balancing',
    '/inventory/adjustments': 'Stock Adjustments',
    '/inventory/alerts': 'Inventory Alerts',
    '/inventory/reports': 'Inventory Reports',
    '/procurement/purchase-orders': 'Purchase Orders',
    '/procurement/suppliers': 'Suppliers',

    '/orders': 'Orders',
    '/orders/pending': 'Pending Orders',
    '/orders/fulfillment': 'Fulfillment',
    '/orders/returns': 'Returns',
    '/orders/new': 'Create Order',
    '/customers': 'Customers',
    '/customers/new': 'Add Customer',
    '/customers/groups/new': 'Add Customer Group',
    '/customers/pricing-tiers/new': 'Add Pricing Tier',
    '/customers/bulk-pricing': 'Bulk Pricing',
    '/billing': 'Billing & Payments',
    '/billing/dashboard': 'Billing Dashboard',
    '/billing/payments': 'Payment Management',
    '/billing/invoices': 'Invoice Management',
    '/billing/reconciliation': 'Payment Reconciliation',
    '/billing/customer-history': 'Customer Payment History',
    '/analytics': 'Analytics Dashboard',
    '/analytics/reports': 'Report Generator',
    '/analytics/audit-logs': 'Audit Logs',
    '/cms': 'CMS Pages',

    // Unified Configuration Routes
    '/configuration': 'Configuration Dashboard',
    '/configuration/dashboard': 'Configuration Dashboard',
    '/configuration/payment': 'Payment Gateway',
    '/configuration/shipping': 'Shipping Provider',
    '/configuration/email': 'Email Service',
    '/configuration/sms': 'SMS Gateway',
    '/configuration/whatsapp': 'WhatsApp Business',
    '/configuration/health': 'Health Monitoring',
    '/configuration/analytics': 'Integration Analytics',
    '/configuration/templates': 'Notification Templates',
    '/configuration/webhooks': 'Webhook Management',
    '/configuration/audit': 'Configuration Audit Trail',

    // System Settings Routes
    '/settings/general/company': 'Company Information',
    '/settings/general/rules': 'Business Rules',
    '/settings/general/localization': 'Localization',
    '/settings/security/users': 'User Management',
    '/settings/security/roles': 'Role Management',
    '/settings/security/permissions': 'Permission Matrix',
    '/settings/security/audit': 'Security Audit Logs',
    '/settings/system/database': 'Database Management',
    '/settings/system/cache': 'Cache Management',
    '/settings/system/backup': 'Backup & Restore',
    '/settings/system/health': 'System Health',
  }

  // Section mapping for better organization
  const sectionMap: Record<string, string> = {
    '/catalogues': 'Catalog Management',
    '/collections': 'Catalog Management',
    '/categories': 'Catalog Management',
    '/products': 'Product Management',
    '/inventory': 'Inventory Management',
    '/procurement': 'Procurement',
    '/orders': 'Order Management',
    '/customers': 'Customer Management',
    '/billing': 'Billing & Payments',
    '/analytics': 'Analytics & Reports',
    '/cms': 'Content Management',
    '/configuration': 'Configuration',
    '/settings': 'System Settings',
  }

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const breadcrumbs: BreadcrumbItem[] = []

    // Add home if requested
    if (showHome) {
      breadcrumbs.push({
        label: 'Home',
        path: '/dashboard',
        icon: <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />,
      })
    }

    // Build breadcrumbs from path segments
    let currentPath = ''
    
    for (let i = 0; i < pathSegments.length; i++) {
      currentPath += `/${pathSegments[i]}`
      
      // Skip if this is the current page (last segment)
      const isCurrentPage = i === pathSegments.length - 1
      
      // Get label from route map
      let label = routeMap[currentPath]
      
      // If no direct mapping, try to get section name
      if (!label) {
        const sectionPath = `/${pathSegments[0]}`
        const sectionName = sectionMap[sectionPath]
        
        if (sectionName && i === 0) {
          label = sectionName
        } else {
          // Fallback to formatted segment name
          label = pathSegments[i]
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        }
      }

      // Handle special cases for dynamic routes
      if (pathSegments[i] === 'edit' && pathSegments[i + 1]) {
        label = `Edit ${label}`
      } else if (pathSegments[i] === 'view' && pathSegments[i + 1]) {
        label = `View ${label}`
      }

      // Don't add current page to breadcrumbs if it's the last item
      if (!isCurrentPage || breadcrumbs.length === 0) {
        breadcrumbs.push({
          label,
          path: currentPath,
        })
      }
    }

    return breadcrumbs
  }

  const breadcrumbItems = generateBreadcrumbs()

  // Don't render if only home breadcrumb
  if (breadcrumbItems.length <= 1) {
    return null
  }

  return (
    <Box sx={{ mb: 2 }}>
      <MUIBreadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        maxItems={maxItems}
        aria-label="breadcrumb navigation"
        sx={{
          '& .MuiBreadcrumbs-separator': {
            color: theme.palette.text.secondary,
          },
        }}
      >
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1

          if (isLast) {
            return (
              <Typography
                key={item.path}
                color="text.primary"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 500,
                }}
              >
                {item.icon}
                {item.label}
              </Typography>
            )
          }

          return (
            <Link
              key={item.path}
              component={RouterLink}
              to={item.path}
              underline="hover"
              color="inherit"
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              {item.icon}
              {item.label}
            </Link>
          )
        })}
      </MUIBreadcrumbs>
    </Box>
  )
}

export default Breadcrumbs